import { createAuthClient } from 'better-auth/react';

/**
 * Better Auth Client for React
 *
 * This client connects to the Better Auth backend for Google SSO authentication.
 *
 * Usage Examples:
 * ---------------
 *
 * Sign in with Google:
 * ```tsx
 * import { signIn } from '@/lib/auth-client';
 *
 * function LoginButton() {
 *   const handleLogin = async () => {
 *     await signIn.social({
 *       provider: 'google',
 *       callbackURL: '/app', // Redirect after successful login
 *     });
 *   };
 *   return <button onClick={handleLogin}>Sign in with Google</button>;
 * }
 * ```
 *
 * Check session state (React hook):
 * ```tsx
 * import { useSession } from '@/lib/auth-client';
 *
 * function Dashboard() {
 *   const { data: session, isPending } = useSession();
 *
 *   if (isPending) return <div>Loading...</div>;
 *   if (!session) return <Redirect to="/start" />;
 *
 *   return <div>Welcome, {session.user.name}!</div>;
 * }
 * ```
 *
 * Sign out:
 * ```tsx
 * import { signOut } from '@/lib/auth-client';
 *
 * function LogoutButton() {
 *   return (
 *     <button onClick={() => signOut({ fetchOptions: { onSuccess: () => window.location.href = '/' } })}>
 *       Sign Out
 *     </button>
 *   );
 * }
 * ```
 *
 * Get session (imperative):
 * ```tsx
 * import { getSession } from '@/lib/auth-client';
 *
 * async function checkAuth() {
 *   const session = await getSession();
 *   if (session.data) {
 *     console.log('User:', session.data.user);
 *   }
 * }
 * ```
 */
export const authClient = createAuthClient({
  baseURL: import.meta.env.VITE_API_URL || '',
});

// Export individual methods for convenience
export const {
  signIn,
  signUp,
  signOut,
  useSession,
  getSession,
} = authClient;
